package kpt.core.database.banking.dao

import androidx.room3.EntityDeleteOrUpdateAdapter
import androidx.room3.EntityInsertAdapter
import androidx.room3.EntityUpsertAdapter
import androidx.room3.RoomDatabase
import androidx.room3.coroutines.createFlow
import androidx.room3.util.appendPlaceholders
import androidx.room3.util.getColumnIndexOrThrow
import androidx.room3.util.performSuspending
import androidx.sqlite.SQLiteStatement
import kotlin.Boolean
import kotlin.Double
import kotlin.Int
import kotlin.Long
import kotlin.String
import kotlin.Suppress
import kotlin.Unit
import kotlin.collections.List
import kotlin.collections.MutableList
import kotlin.collections.Set
import kotlin.collections.mutableListOf
import kotlin.reflect.KClass
import kotlin.text.StringBuilder
import kotlinx.coroutines.flow.Flow
import kpt.core.database.banking.converter.BankingTypeConverters
import kpt.core.database.banking.entity.BillReminderEntity
import kpt.core.model.banking.BillCategory
import kpt.core.model.banking.Recurrence

@Suppress(names = ["UNCHECKED_CAST", "DEPRECATION", "REDUNDANT_PROJECTION", "REMOVAL", "OPT_IN_USAGE_ERROR", "OPT_IN_USAGE", "MemberExtensionConflict", "CAN_BE_VAL"])
internal class BillReminderDao_Impl(
  __db: RoomDatabase,
) : BillReminderDao {
  private val __db: RoomDatabase

  private val __upsertAdapterOfBillReminderEntity: EntityUpsertAdapter<BillReminderEntity>

  private val __bankingTypeConverters: BankingTypeConverters = BankingTypeConverters()
  init {
    this.__db = __db
    this.__upsertAdapterOfBillReminderEntity = EntityUpsertAdapter<BillReminderEntity>(object : EntityInsertAdapter<BillReminderEntity>() {
      protected override fun createQuery(): String = "INSERT INTO `banking_bill_reminders` (`id`,`name`,`amount`,`dueDay`,`recurrence`,`category`,`enabled`,`reminderDaysBefore`,`createdAtMs`,`updatedAtMs`) VALUES (?,?,?,?,?,?,?,?,?,?)"

      protected override fun bind(statement: SQLiteStatement, entity: BillReminderEntity) {
        statement.bindText(1, entity.id)
        statement.bindText(2, entity.name)
        statement.bindDouble(3, entity.amount)
        statement.bindLong(4, entity.dueDay.toLong())
        val _tmp: String = __bankingTypeConverters.fromRecurrence(entity.recurrence)
        statement.bindText(5, _tmp)
        val _tmp_1: String = __bankingTypeConverters.fromBillCategory(entity.category)
        statement.bindText(6, _tmp_1)
        val _tmp_2: Int = if (entity.enabled) 1 else 0
        statement.bindLong(7, _tmp_2.toLong())
        statement.bindLong(8, entity.reminderDaysBefore.toLong())
        statement.bindLong(9, entity.createdAtMs)
        statement.bindLong(10, entity.updatedAtMs)
      }
    }, object : EntityDeleteOrUpdateAdapter<BillReminderEntity>() {
      protected override fun createQuery(): String = "UPDATE `banking_bill_reminders` SET `id` = ?,`name` = ?,`amount` = ?,`dueDay` = ?,`recurrence` = ?,`category` = ?,`enabled` = ?,`reminderDaysBefore` = ?,`createdAtMs` = ?,`updatedAtMs` = ? WHERE `id` = ?"

      protected override fun bind(statement: SQLiteStatement, entity: BillReminderEntity) {
        statement.bindText(1, entity.id)
        statement.bindText(2, entity.name)
        statement.bindDouble(3, entity.amount)
        statement.bindLong(4, entity.dueDay.toLong())
        val _tmp: String = __bankingTypeConverters.fromRecurrence(entity.recurrence)
        statement.bindText(5, _tmp)
        val _tmp_1: String = __bankingTypeConverters.fromBillCategory(entity.category)
        statement.bindText(6, _tmp_1)
        val _tmp_2: Int = if (entity.enabled) 1 else 0
        statement.bindLong(7, _tmp_2.toLong())
        statement.bindLong(8, entity.reminderDaysBefore.toLong())
        statement.bindLong(9, entity.createdAtMs)
        statement.bindLong(10, entity.updatedAtMs)
        statement.bindText(11, entity.id)
      }
    })
  }

  public override suspend fun upsert(entity: BillReminderEntity): Unit = performSuspending(__db, false, true) { _connection ->
    __upsertAdapterOfBillReminderEntity.upsert(_connection, entity)
  }

  public override fun observeAll(): Flow<List<BillReminderEntity>> {
    val _sql: String = "SELECT * FROM banking_bill_reminders ORDER BY dueDay ASC, createdAtMs ASC"
    return createFlow(__db, false, arrayOf("banking_bill_reminders")) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfAmount: Int = getColumnIndexOrThrow(_stmt, "amount")
        val _columnIndexOfDueDay: Int = getColumnIndexOrThrow(_stmt, "dueDay")
        val _columnIndexOfRecurrence: Int = getColumnIndexOrThrow(_stmt, "recurrence")
        val _columnIndexOfCategory: Int = getColumnIndexOrThrow(_stmt, "category")
        val _columnIndexOfEnabled: Int = getColumnIndexOrThrow(_stmt, "enabled")
        val _columnIndexOfReminderDaysBefore: Int = getColumnIndexOrThrow(_stmt, "reminderDaysBefore")
        val _columnIndexOfCreatedAtMs: Int = getColumnIndexOrThrow(_stmt, "createdAtMs")
        val _columnIndexOfUpdatedAtMs: Int = getColumnIndexOrThrow(_stmt, "updatedAtMs")
        val _result: MutableList<BillReminderEntity> = mutableListOf()
        while (_stmt.step()) {
          val _item: BillReminderEntity
          val _tmpId: String
          _tmpId = _stmt.getText(_columnIndexOfId)
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpAmount: Double
          _tmpAmount = _stmt.getDouble(_columnIndexOfAmount)
          val _tmpDueDay: Int
          _tmpDueDay = _stmt.getLong(_columnIndexOfDueDay).toInt()
          val _tmpRecurrence: Recurrence
          val _tmp: String
          _tmp = _stmt.getText(_columnIndexOfRecurrence)
          _tmpRecurrence = __bankingTypeConverters.toRecurrence(_tmp)
          val _tmpCategory: BillCategory
          val _tmp_1: String
          _tmp_1 = _stmt.getText(_columnIndexOfCategory)
          _tmpCategory = __bankingTypeConverters.toBillCategory(_tmp_1)
          val _tmpEnabled: Boolean
          val _tmp_2: Int
          _tmp_2 = _stmt.getLong(_columnIndexOfEnabled).toInt()
          _tmpEnabled = _tmp_2 != 0
          val _tmpReminderDaysBefore: Int
          _tmpReminderDaysBefore = _stmt.getLong(_columnIndexOfReminderDaysBefore).toInt()
          val _tmpCreatedAtMs: Long
          _tmpCreatedAtMs = _stmt.getLong(_columnIndexOfCreatedAtMs)
          val _tmpUpdatedAtMs: Long
          _tmpUpdatedAtMs = _stmt.getLong(_columnIndexOfUpdatedAtMs)
          _item = BillReminderEntity(_tmpId,_tmpName,_tmpAmount,_tmpDueDay,_tmpRecurrence,_tmpCategory,_tmpEnabled,_tmpReminderDaysBefore,_tmpCreatedAtMs,_tmpUpdatedAtMs)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override fun observeUpcoming(dueDays: Set<Int>): Flow<List<BillReminderEntity>> {
    val _stringBuilder: StringBuilder = StringBuilder()
    _stringBuilder.append("SELECT * FROM banking_bill_reminders WHERE enabled = 1 AND dueDay IN (")
    val _inputSize: Int = dueDays.size
    appendPlaceholders(_stringBuilder, _inputSize)
    _stringBuilder.append(") ORDER BY dueDay ASC, createdAtMs ASC")
    val _sql: String = _stringBuilder.toString()
    return createFlow(__db, false, arrayOf("banking_bill_reminders")) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        for (_item: Int in dueDays) {
          _stmt.bindLong(_argIndex, _item.toLong())
          _argIndex++
        }
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfAmount: Int = getColumnIndexOrThrow(_stmt, "amount")
        val _columnIndexOfDueDay: Int = getColumnIndexOrThrow(_stmt, "dueDay")
        val _columnIndexOfRecurrence: Int = getColumnIndexOrThrow(_stmt, "recurrence")
        val _columnIndexOfCategory: Int = getColumnIndexOrThrow(_stmt, "category")
        val _columnIndexOfEnabled: Int = getColumnIndexOrThrow(_stmt, "enabled")
        val _columnIndexOfReminderDaysBefore: Int = getColumnIndexOrThrow(_stmt, "reminderDaysBefore")
        val _columnIndexOfCreatedAtMs: Int = getColumnIndexOrThrow(_stmt, "createdAtMs")
        val _columnIndexOfUpdatedAtMs: Int = getColumnIndexOrThrow(_stmt, "updatedAtMs")
        val _result: MutableList<BillReminderEntity> = mutableListOf()
        while (_stmt.step()) {
          val _item_1: BillReminderEntity
          val _tmpId: String
          _tmpId = _stmt.getText(_columnIndexOfId)
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpAmount: Double
          _tmpAmount = _stmt.getDouble(_columnIndexOfAmount)
          val _tmpDueDay: Int
          _tmpDueDay = _stmt.getLong(_columnIndexOfDueDay).toInt()
          val _tmpRecurrence: Recurrence
          val _tmp: String
          _tmp = _stmt.getText(_columnIndexOfRecurrence)
          _tmpRecurrence = __bankingTypeConverters.toRecurrence(_tmp)
          val _tmpCategory: BillCategory
          val _tmp_1: String
          _tmp_1 = _stmt.getText(_columnIndexOfCategory)
          _tmpCategory = __bankingTypeConverters.toBillCategory(_tmp_1)
          val _tmpEnabled: Boolean
          val _tmp_2: Int
          _tmp_2 = _stmt.getLong(_columnIndexOfEnabled).toInt()
          _tmpEnabled = _tmp_2 != 0
          val _tmpReminderDaysBefore: Int
          _tmpReminderDaysBefore = _stmt.getLong(_columnIndexOfReminderDaysBefore).toInt()
          val _tmpCreatedAtMs: Long
          _tmpCreatedAtMs = _stmt.getLong(_columnIndexOfCreatedAtMs)
          val _tmpUpdatedAtMs: Long
          _tmpUpdatedAtMs = _stmt.getLong(_columnIndexOfUpdatedAtMs)
          _item_1 = BillReminderEntity(_tmpId,_tmpName,_tmpAmount,_tmpDueDay,_tmpRecurrence,_tmpCategory,_tmpEnabled,_tmpReminderDaysBefore,_tmpCreatedAtMs,_tmpUpdatedAtMs)
          _result.add(_item_1)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override fun observeById(id: String): Flow<BillReminderEntity?> {
    val _sql: String = "SELECT * FROM banking_bill_reminders WHERE id = ? LIMIT 1"
    return createFlow(__db, false, arrayOf("banking_bill_reminders")) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, id)
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfAmount: Int = getColumnIndexOrThrow(_stmt, "amount")
        val _columnIndexOfDueDay: Int = getColumnIndexOrThrow(_stmt, "dueDay")
        val _columnIndexOfRecurrence: Int = getColumnIndexOrThrow(_stmt, "recurrence")
        val _columnIndexOfCategory: Int = getColumnIndexOrThrow(_stmt, "category")
        val _columnIndexOfEnabled: Int = getColumnIndexOrThrow(_stmt, "enabled")
        val _columnIndexOfReminderDaysBefore: Int = getColumnIndexOrThrow(_stmt, "reminderDaysBefore")
        val _columnIndexOfCreatedAtMs: Int = getColumnIndexOrThrow(_stmt, "createdAtMs")
        val _columnIndexOfUpdatedAtMs: Int = getColumnIndexOrThrow(_stmt, "updatedAtMs")
        val _result: BillReminderEntity?
        if (_stmt.step()) {
          val _tmpId: String
          _tmpId = _stmt.getText(_columnIndexOfId)
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpAmount: Double
          _tmpAmount = _stmt.getDouble(_columnIndexOfAmount)
          val _tmpDueDay: Int
          _tmpDueDay = _stmt.getLong(_columnIndexOfDueDay).toInt()
          val _tmpRecurrence: Recurrence
          val _tmp: String
          _tmp = _stmt.getText(_columnIndexOfRecurrence)
          _tmpRecurrence = __bankingTypeConverters.toRecurrence(_tmp)
          val _tmpCategory: BillCategory
          val _tmp_1: String
          _tmp_1 = _stmt.getText(_columnIndexOfCategory)
          _tmpCategory = __bankingTypeConverters.toBillCategory(_tmp_1)
          val _tmpEnabled: Boolean
          val _tmp_2: Int
          _tmp_2 = _stmt.getLong(_columnIndexOfEnabled).toInt()
          _tmpEnabled = _tmp_2 != 0
          val _tmpReminderDaysBefore: Int
          _tmpReminderDaysBefore = _stmt.getLong(_columnIndexOfReminderDaysBefore).toInt()
          val _tmpCreatedAtMs: Long
          _tmpCreatedAtMs = _stmt.getLong(_columnIndexOfCreatedAtMs)
          val _tmpUpdatedAtMs: Long
          _tmpUpdatedAtMs = _stmt.getLong(_columnIndexOfUpdatedAtMs)
          _result = BillReminderEntity(_tmpId,_tmpName,_tmpAmount,_tmpDueDay,_tmpRecurrence,_tmpCategory,_tmpEnabled,_tmpReminderDaysBefore,_tmpCreatedAtMs,_tmpUpdatedAtMs)
        } else {
          _result = null
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun getById(id: String): BillReminderEntity? {
    val _sql: String = "SELECT * FROM banking_bill_reminders WHERE id = ? LIMIT 1"
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, id)
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfAmount: Int = getColumnIndexOrThrow(_stmt, "amount")
        val _columnIndexOfDueDay: Int = getColumnIndexOrThrow(_stmt, "dueDay")
        val _columnIndexOfRecurrence: Int = getColumnIndexOrThrow(_stmt, "recurrence")
        val _columnIndexOfCategory: Int = getColumnIndexOrThrow(_stmt, "category")
        val _columnIndexOfEnabled: Int = getColumnIndexOrThrow(_stmt, "enabled")
        val _columnIndexOfReminderDaysBefore: Int = getColumnIndexOrThrow(_stmt, "reminderDaysBefore")
        val _columnIndexOfCreatedAtMs: Int = getColumnIndexOrThrow(_stmt, "createdAtMs")
        val _columnIndexOfUpdatedAtMs: Int = getColumnIndexOrThrow(_stmt, "updatedAtMs")
        val _result: BillReminderEntity?
        if (_stmt.step()) {
          val _tmpId: String
          _tmpId = _stmt.getText(_columnIndexOfId)
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpAmount: Double
          _tmpAmount = _stmt.getDouble(_columnIndexOfAmount)
          val _tmpDueDay: Int
          _tmpDueDay = _stmt.getLong(_columnIndexOfDueDay).toInt()
          val _tmpRecurrence: Recurrence
          val _tmp: String
          _tmp = _stmt.getText(_columnIndexOfRecurrence)
          _tmpRecurrence = __bankingTypeConverters.toRecurrence(_tmp)
          val _tmpCategory: BillCategory
          val _tmp_1: String
          _tmp_1 = _stmt.getText(_columnIndexOfCategory)
          _tmpCategory = __bankingTypeConverters.toBillCategory(_tmp_1)
          val _tmpEnabled: Boolean
          val _tmp_2: Int
          _tmp_2 = _stmt.getLong(_columnIndexOfEnabled).toInt()
          _tmpEnabled = _tmp_2 != 0
          val _tmpReminderDaysBefore: Int
          _tmpReminderDaysBefore = _stmt.getLong(_columnIndexOfReminderDaysBefore).toInt()
          val _tmpCreatedAtMs: Long
          _tmpCreatedAtMs = _stmt.getLong(_columnIndexOfCreatedAtMs)
          val _tmpUpdatedAtMs: Long
          _tmpUpdatedAtMs = _stmt.getLong(_columnIndexOfUpdatedAtMs)
          _result = BillReminderEntity(_tmpId,_tmpName,_tmpAmount,_tmpDueDay,_tmpRecurrence,_tmpCategory,_tmpEnabled,_tmpReminderDaysBefore,_tmpCreatedAtMs,_tmpUpdatedAtMs)
        } else {
          _result = null
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override fun count(): Flow<Int> {
    val _sql: String = "SELECT COUNT(*) FROM banking_bill_reminders"
    return createFlow(__db, false, arrayOf("banking_bill_reminders")) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        val _result: Int
        if (_stmt.step()) {
          val _tmp: Int
          _tmp = _stmt.getLong(0).toInt()
          _result = _tmp
        } else {
          _result = 0
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun deleteById(id: String) {
    val _sql: String = "DELETE FROM banking_bill_reminders WHERE id = ?"
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, id)
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun deleteAll() {
    val _sql: String = "DELETE FROM banking_bill_reminders"
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public companion object {
    public fun getRequiredColumnConverters(): List<KClass<*>> = emptyList()

    public fun getRequiredDaoReturnTypeConverters(): List<KClass<*>> = emptyList()
  }
}
